import { useEffect } from 'react';
import { AxiosError } from 'axios';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import {
  useCreatePurchaseOrder,
  useGiftCardData,
  useGtmBillingProduct
} from '@/modules/perseo-billing/hooks';
import type {
  IPaymentRequest,
  ISinglePayment
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { setIsProcessingPayment } from '@/modules/perseo-billing/store/slices/billing';

const checkIfPaymentIsTCOM = (selectedPaymentMethods: IPaymentRequest) => {
  return selectedPaymentMethods?.payments?.find(
    (payment) =>
      payment.paymentProcessor === PaymentProcessor.TRANSBANK ||
      payment.paymentProcessor === PaymentProcessor.TRANSBANK_CENCOSUD_CARD
  );
};

const useConstructPayment = () => {
  const billing = useAppSelector((state) => state.billing);
  const { selectedPaymentMethods } = billing;
  const { cartHasGiftCardPayment, getGiftCardPayment } = useGiftCardData();

  const getPayments = (): ISinglePayment[] => {
    if (!selectedPaymentMethods?.payments) return [];
    const giftCardPayment = getGiftCardPayment();

    const payments = [selectedPaymentMethods?.payments[0]];

    const hasGiftCard = cartHasGiftCardPayment();
    if (hasGiftCard) {
      payments.push(giftCardPayment);
    }

    return payments;
  };

  return {
    getPayments
  };
};

const useSpreedlyPayment = () => {
  const dispatch = useAppDispatch();
  const billing = useAppSelector((state) => state.billing);
  const { data: cart } = useAppSelector((state) => state.cart);

  const { selectedPaymentMethods } = billing;
  const { mutate: createPurchaseOrder } = useCreatePurchaseOrder();
  const { paymentButtonGtm, saveCardPaymentMethod } = useGtmBillingProduct();

  const { getPayments } = useConstructPayment();

  useEffect(() => {
    try {
      if (!cart) return;
      if (!selectedPaymentMethods) return;
      if (selectedPaymentMethods?.payments?.length === 0) return;

      const isTCOM = checkIfPaymentIsTCOM(selectedPaymentMethods);
      if (!isTCOM) return;

      dispatch(setIsProcessingPayment(true));
      paymentButtonGtm();
      saveCardPaymentMethod();

      const payments = getPayments();
      const payload = {
        payments: payments
      };

      createPurchaseOrder(payload);
    } catch (error) {
      console.error('Payment processing error:', error);
      const isAxiosError = error instanceof AxiosError;
      dispatch(setIsProcessingPayment(false));
      if (isAxiosError) return;
    }
  }, [
    cart,
    createPurchaseOrder,
    dispatch,
    getPayments,
    paymentButtonGtm,
    saveCardPaymentMethod,
    selectedPaymentMethods
  ]);
};
export default useSpreedlyPayment;
